import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft, ArrowRight, Save, Phone, ClipboardList, UserCog, ScrollText, StickyNote, Mail, Package,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLeadForm } from "@/hooks/useLeadForm";
import CustomFieldInput from "@/components/leads/CustomFieldInput";
import GroupedSelect from "@/components/leads/GroupedSelect";
import LeadHistoryPanel from "@/components/leads/LeadHistoryPanel";
import ReassignDialog from "@/components/leads/ReassignDialog";

const LANG_LABEL = { en: "English", ar: "Arabic", hi: "Hindi" };

/** Starlink outbound-campaign agent screen: lead/order → script → internal note → capture → prev/next. */
export default function CampaignLeadDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const lf = useLeadForm(id);
  const { lead, related, loading, saving, save, form, setForm, customFields, dropdowns,
          dispOptions, followUpNeeded, isDisabledSave, needsDisposition, labelOf, load } = lf;
  const [script, setScript] = useState(null);
  const [nav, setNav] = useState(null);
  const [greeting, setGreeting] = useState(() => {
    const h = new Date().getHours();
    return h < 12 ? "morning" : h < 17 ? "afternoon" : "evening";
  });
  const [lang, setLang] = useState("en");
  const [reassignOpen, setReassignOpen] = useState(false);

  const loadSide = useCallback(() => {
    api.get(`/leads/${id}/script`).then((r) => setScript(r.data)).catch(() => setScript(null));
    api.get(`/leads/${id}/queue-nav`).then((r) => setNav(r.data)).catch(() => setNav(null));
  }, [id]);
  useEffect(() => { loadSide(); setLang("en"); }, [loadSide]);

  useEffect(() => {
    if (searchParams.get("src") === "genesys" && lead) toast.info("Screen-pop: call landed on this lead");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.id]);

  if (loading) return <div className="text-slate-400 p-6 text-sm">Loading lead…</div>;
  if (!lead) {
    return (
      <div className="p-6">
        <div className="text-sm text-rose-600">Lead not found.</div>
        <Button variant="link" onClick={() => navigate("/leads")}>Back to Leads</Button>
      </div>
    );
  }

  const camp = lead.campaign || {};
  const mobile = camp.mobile || lead.numbers?.[0]?.e164;
  const isRefund = !!camp.refund_amount;
  const captureFields = customFields.filter((f) => f.section === "capture" && !f.read_only);
  const bodyLangs = Object.keys(script?.script?.body || {}).filter((k) => script.script.body[k]);
  const onSave = async () => {
    const missing = captureFields.filter((f) => f.required && !String((form.custom || {})[f.key] || "").trim());
    if (missing.length && form.disposition) {
      toast.error(`Please fill: ${missing.map((f) => f.label).join(", ")}`);
      return;
    }
    await save();
    loadSide();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-4" data-testid="campaign-lead-detail">
      {/* Header */}
      <div className="col-span-full flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate("/leads")} data-testid="lead-back">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Outbound campaign · iPhone 18 pre-order</div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-heading text-lg font-semibold text-slate-900" data-testid="campaign-customer-name">{lead.customer_name}</h1>
              <Badge variant="outline" className="font-mono text-[10px]" data-testid="campaign-mobile">{mobile}</Badge>
              {script?.type?.label && (
                <Badge className="text-[10px] text-white" style={{ background: script.type.color || "#4F46E5" }}
                       data-testid="campaign-type-badge">{script.type.label}</Badge>
              )}
              {lead.pending_language_assignment && (
                <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-300">Arabic — pending assignment</Badge>
              )}
              {lead.status === "closed" && <Badge variant="outline" className="text-[10px] text-slate-500">Closed</Badge>}
            </div>
            <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-slate-500">
              <span>Assignee:</span>
              <span className="font-medium text-slate-700" data-testid="lead-assignee">{lead.assigned_to || "unassigned"}</span>
              {hasPerm("leads.reassign") && (
                <button type="button" onClick={() => setReassignOpen(true)}
                        className="inline-flex items-center gap-1 text-[10px] font-medium text-indigo-600 hover:text-indigo-800"
                        data-testid="lead-reassign-btn">
                  <UserCog className="w-3 h-3" /> Reassign
                </button>
              )}
            </div>
          </div>
        </div>
        <QueueNav nav={nav} navigate={navigate} />
      </div>

      <div className="space-y-4">
        {/* 1. Lead / order */}
        <Card data-testid="campaign-order-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center justify-between gap-2">
              <span className="flex items-center gap-2"><ClipboardList className="w-4 h-4 text-slate-500" /> Lead / order</span>
              {script?.status?.header && (
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded"
                      style={{ background: `${script.status.color || "#64748B"}20`, color: script.status.color || "#334155" }}
                      data-testid="campaign-status-header">
                  {script.status.header}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <Field label="Mobile" value={<span className="flex items-center gap-1"><Phone className="w-3 h-3 text-slate-400" />{mobile}</span>} mono />
            <Field label="Customer" value={lead.customer_name} />
            <Field label="Email" value={camp.email ? <span className="flex items-center gap-1"><Mail className="w-3 h-3 text-slate-400" />{camp.email}</span> : "—"} />
            <Field label="Order number(s)" value={(camp.order_numbers || []).join(", ") || "—"} mono />
            {isRefund && (
              <>
                <Field label="Refund amount" value={`QAR ${camp.refund_amount}`} mono testid="campaign-refund-amount" />
                <Field label="Bank reference" value={camp.refund_ref || "—"} mono testid="campaign-refund-ref" />
              </>
            )}
            <div className="col-span-2 md:col-span-4">
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">Items</Label>
              <div className="mt-1 divide-y divide-slate-100 border border-slate-200 rounded">
                {(camp.orders || []).map((o, i) => (
                  <div key={i} className="flex items-start gap-2 px-2 py-1.5" data-testid={`campaign-item-${i}`}>
                    <Package className="w-3 h-3 text-slate-400 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-slate-800 break-words">{o.item}</div>
                      <div className="text-[10px] text-slate-400">{[o.sku, o.qty ? `Qty ${o.qty}` : null, o.sl_no ? `#${o.sl_no}` : null].filter(Boolean).join(" · ")}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {(camp.previous_notes || []).length > 0 && (
              <div className="col-span-2 md:col-span-4">
                <Label className="text-[10px] uppercase tracking-wider text-slate-400">Notes from earlier calling round</Label>
                <div className="mt-1 text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="campaign-previous-notes">
                  {camp.previous_notes.join(" · ")}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 2. Script */}
        <Card data-testid="campaign-script-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center justify-between gap-2 flex-wrap">
              <span className="flex items-center gap-2"><ScrollText className="w-4 h-4 text-slate-500" /> Call script
                {script?.script?.version && <span className="text-[10px] text-slate-400 font-normal">v{script.script.version}</span>}
              </span>
              {script?.script && (
                <div className="flex items-center gap-2">
                  <Tabs value={greeting} onValueChange={setGreeting}>
                    <TabsList className="h-7 bg-slate-100 p-0.5">
                      {["morning", "afternoon", "evening"].map((g) => (
                        <TabsTrigger key={g} value={g} className="text-[10px] h-6 px-2 capitalize" data-testid={`greeting-${g}`}>{g}</TabsTrigger>
                      ))}
                    </TabsList>
                  </Tabs>
                  {bodyLangs.length > 1 && (
                    <Tabs value={lang} onValueChange={setLang}>
                      <TabsList className="h-7 bg-slate-100 p-0.5">
                        {bodyLangs.map((k) => (
                          <TabsTrigger key={k} value={k} className="text-[10px] h-6 px-2" data-testid={`script-lang-${k}`}>{LANG_LABEL[k] || k}</TabsTrigger>
                        ))}
                      </TabsList>
                    </Tabs>
                  )}
                </div>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm leading-relaxed text-slate-800 space-y-2">
            {script?.script ? (
              <>
                <p className="font-medium" data-testid="script-greeting">{script.script.greetings?.[greeting]}</p>
                <p className="whitespace-pre-wrap" dir={lang === "ar" ? "rtl" : "ltr"} data-testid="script-body">
                  {script.script.body?.[lang] || script.script.body?.en}
                </p>
              </>
            ) : (
              <div className="text-xs text-slate-400" data-testid="script-missing">
                No published script for this Type/Status yet — add one in Campaign settings → Call Scripts.
              </div>
            )}
          </CardContent>
        </Card>

        {/* 3. Internal note */}
        {script?.script?.internal_note && (
          <div className="flex items-start gap-2 text-xs text-indigo-900 bg-indigo-50 border border-indigo-200 rounded px-3 py-2"
               data-testid="script-internal-note">
            <StickyNote className="w-3.5 h-3.5 mt-0.5 shrink-0 text-indigo-500" />
            <div><span className="font-semibold">Internal note — suggested action (not read out): </span>{script.script.internal_note}</div>
          </div>
        )}

        {/* 4. Capture */}
        <Card data-testid="campaign-capture-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Call capture</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {captureFields.map((f) => (
              <CustomFieldInput key={f.id} field={f} value={(form.custom || {})[f.key] || ""} dropdowns={dropdowns}
                onChange={(v) => setForm((prev) => ({ ...prev, custom: { ...(prev.custom || {}), [f.key]: v } }))} />
            ))}
            <div className="md:col-span-2">
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">{labelOf("remarks", "Remarks")}</Label>
              <Textarea value={form.remarks || ""} rows={3} className="mt-1 text-xs resize-none" data-testid="remarks-input"
                        placeholder="What the customer said, anything the next agent should know"
                        onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))} />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">
                {labelOf("disposition", "Final disposition")}<span className="text-rose-500 ml-0.5">*</span>
              </Label>
              <div className={`mt-1 ${needsDisposition ? "rounded ring-1 ring-rose-300" : ""}`}>
                <GroupedSelect value={form.disposition} options={dispOptions} placeholder="Select final disposition"
                               testid="disposition-select" onChange={(v) => setForm((f) => ({ ...f, disposition: v }))} />
              </div>
              {form.disposition === "AR" && (
                <div className="text-[10px] text-violet-700 mt-1" data-testid="arabic-hint">Saving routes this lead to an Arabic-speaking agent.</div>
              )}
            </div>
            {form.disposition === "CB" && (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-400">
                  {labelOf("follow_up_at", "Callback date & time")}{followUpNeeded && <span className="text-rose-500 ml-0.5">*</span>}
                </Label>
                <Input type="datetime-local" value={form.follow_up_at ? form.follow_up_at.slice(0, 16) : ""}
                       onChange={(e) => setForm((f) => ({ ...f, follow_up_at: e.target.value }))}
                       className="h-8 mt-1 text-xs" data-testid="follow-up-input" />
                {!form.follow_up_at && <div className="text-[10px] text-rose-500 mt-0.5">Required — a reminder will be sent to you before the callback.</div>}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="flex items-center justify-between pb-6" data-testid="campaign-footer">
          <QueueNav nav={nav} navigate={navigate} />
          <Button size="sm" onClick={onSave} disabled={saving || isDisabledSave}
                  className="bg-indigo-600 hover:bg-indigo-700 px-8" data-testid="lead-save">
            <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <LeadHistoryPanel lead={lead} related={related} />
      <ReassignDialog open={reassignOpen} onOpenChange={setReassignOpen} leadIds={[lead.id]}
                      arabicHint={lead.call_language === "arabic_only" || !!lead.pending_language_assignment} onDone={load} />
    </div>
  );
}

function QueueNav({ nav, navigate }) {
  if (!nav) return <div />;
  return (
    <div className="flex items-center gap-1.5" data-testid="queue-nav">
      <Button variant="outline" size="sm" className="h-7 text-xs" disabled={!nav.prev_id}
              onClick={() => navigate(`/leads/${nav.prev_id}`)} data-testid="queue-prev">
        <ArrowLeft className="w-3 h-3 mr-1" /> Prev
      </Button>
      <span className="text-[11px] text-slate-500 font-mono" data-testid="queue-position">
        {nav.position} / {nav.total}{nav.scope === "mine" ? " · my queue" : ""}
      </span>
      <Button variant="outline" size="sm" className="h-7 text-xs" disabled={!nav.next_id}
              onClick={() => navigate(`/leads/${nav.next_id}`)} data-testid="queue-next">
        Next <ArrowRight className="w-3 h-3 ml-1" />
      </Button>
    </div>
  );
}

function Field({ label, value, mono, testid }) {
  return (
    <div data-testid={testid}>
      <Label className="text-[10px] uppercase tracking-wider text-slate-400">{label}</Label>
      <div className={`mt-1 text-slate-800 ${mono ? "font-mono text-[11px]" : ""}`}>{value}</div>
    </div>
  );
}
